import { defineCopy } from '@floorfile/i18n'

/**
 * Work orders, preventive plans, the Kanban board, the calendar and the
 * technician runner (localization plan Phase 5a, chunk 5a-4).
 *
 * ## This slice pays off the debt chunk 5a-3 deliberately left
 *
 * `WoStatus`, `WorkPriority`, `WoSource`, `FreqUnit`, the plan's assignee mode
 * and the predictive-analysis status are all declared in
 * `contracts/maintenance`, and all six were reaching the screen **raw** — on
 * this module's own pages *and* on the equipment page's embedded tabs. 5a-3
 * recorded them rather than writing a second map in `equipment/copy.ts`; they
 * live here now, and `maintenanceCopy` is exported from this feature's index so
 * equipment reads the same words. One owner per enum — the slice that owns the
 * enum, not the one that happens to render it.
 *
 * ## `WO_STATUS_LABELS` and `PRIORITY_LABEL`
 *
 * `WO_STATUS_LABELS` was a module constant in `WorkOrderRunPage.tsx` — the
 * runner had proper words while the register two clicks away rendered
 * `IN_PROGRESS`. Its English is preserved exactly; the register is what changed.
 *
 * **`PRIORITY_LABEL` and `priorityLabel()` were DELETED from `@floorfile/ui`.**
 * They were English display text in a package that cannot reach a locale — the
 * third instance of that defect, after `ATTACHMENT_CONTEXT_LABEL` in
 * `@floorfile/contracts` and `EQUIPMENT_STATE_LABEL` in `@floorfile/ui` itself —
 * and by the time this chunk started they had **zero consumers**: every screen
 * that needed the words had written them again by hand. `PRIORITY_TONE` stays,
 * because a tone is a colour decision and is the same in every language.
 *
 * The four other modules with a `LOW | MEDIUM | HIGH | CRITICAL` ladder (CARs,
 * audit findings, automation actions, supplier CARs) each declare their **own**
 * Zod enum. They are separate enums that happen to share members, so each owns
 * its own labels when its slice migrates — that is not the duplication this
 * header is about.
 *
 * ## `frequency` is a plural record per unit, and it fixed two English bugs
 *
 * The interval was rendered two different ways for the same fact. The plan
 * builder's live preview singularised by **chopping the trailing `s`**
 * (`unit.toLowerCase().slice(0, -1)`); the plans list did not singularise at
 * all and printed `Every 1 months`. Both are English morphology written as
 * string surgery, and neither is reachable from a translator. One plural record
 * per unit replaces both, so the two surfaces now agree and `Intl.PluralRules`
 * decides the form.
 */
export const maintenanceCopy = defineCopy('maintenance', {
  offlineAction: {
    completeStep: 'Complete work-order step',
    reopenStep: 'Reopen work-order step',
    /** F5.3 */
    stepPhotos: 'Save work-order step photos',
    /** F6.4 */
    flagStep: 'Flag a problem on a work-order step',
    completeWorkOrder: 'Complete work order',
    /** F9: hold and resume queue offline too. */
    holdWorkOrder: 'Put work order on hold',
    resumeWorkOrder: 'Resume work order',
  },
  /**
   * The same values read INSIDE a sentence — "Raised this work order (manual)".
   * Not `source.MANUAL.toLowerCase()`: case is a property of the language, and
   * German capitalises nouns mid-sentence where English does not.
   */
  sourceInline: {
    PLAN: 'plan',
    CORRECTIVE: 'corrective',
    MANUAL: 'manual',
    PREDICTIVE: 'predictive',
  },

  /** The predictive-analysis lifecycle. Rendered on the EQUIPMENT page. */
  predictiveStatus: {
    ACTIVE: 'Active',
    RESOLVED: 'Resolved',
    DISMISSED: 'Dismissed',
  },

  /** Who a plan's generated work orders go to. Rendered on both pages. */
  assigneeMode: {
    RESOLVED: 'Engine-assigned',
    FIXED: 'Fixed',
  },

  /**
   * `FreqUnit` as a whole interval phrase — see the header on why this is one
   * plural record per unit rather than `Every {n} {unit}`.
   */
  frequency: {
    DAYS: { one: 'Every day', other: 'Every {count} days' },
    WEEKS: { one: 'Every week', other: 'Every {count} weeks' },
    MONTHS: { one: 'Every month', other: 'Every {count} months' },
    /** A meter hour is a reading, not a calendar unit — it always carries the number. */
    METER_HOURS: { one: 'Every {count} meter hour', other: 'Every {count} meter hours' },
    /** F24A — any counter (cycles, a company's own); the counter is named beside it. */
    METER: { one: 'Every {count} on its counter', other: 'Every {count} on its counter' },
  },
  /** The bare unit, for the picker's options. Lowercase: they follow a number. */
  frequencyUnit: {
    DAYS: 'days',
    WEEKS: 'weeks',
    MONTHS: 'months',
    METER_HOURS: 'meter hours',
    METER: 'on a counter',
  },

  page: {
    title: 'Work orders',
    description: 'Work orders and the preventive plans that generate them.',
    /** Tab LABELS keyed by the tab's own id — the id is the state value. */
    tabs: 'Maintenance views',
    tab: {
      'Work orders': 'Work orders',
      Plans: 'Plans',
      Calendar: 'Calendar',
      /** Fiix audit Phase 3.1: navigates to /maintenance/requests. */
      Requests: 'Requests',
      /** F4 — work-order templates. */
      Templates: 'Templates',
    },
    viewSwitcher: 'Work order view',
    viewTable: 'Table',
    viewBoard: 'Board',
    newWorkOrder: '+ New Work Order',
    mine: 'My work',
    /** Fiix audit Phase 1: the filter bar. */
    searchPlaceholder: 'Search number, title or asset…',
    quick: {
      open: 'All open',
      mine: 'Assigned to me',
      overdue: 'Overdue',
      week: 'Due this week',
    },
    filter: {
      dueFrom: 'Due from',
      dueTo: 'Due to',
      source: 'Source',
      /** F8.2. */
      team: 'Team',
      /** F9: what the on-hold work is waiting for. */
      holdReason: 'Hold reason',
      sourceValue: {
        PLAN: 'Preventive (plan)',
        CORRECTIVE: 'Corrective (downtime)',
        MANUAL: 'Manual',
      },
    },
    loadingBoard: 'Loading board…',
    region: 'Work orders',
    /** Lowercase: read inside "Sorted by {field}, ascending". */
    sortField: {
      woNumber: 'number',
      title: 'title',
      priority: 'priority',
      status: 'status',
      dueAt: 'due date',
      createdAt: 'opened',
    },
    selectAll: 'Select every work order on this page',
    clearSelection: 'Clear selection on this page',
    selectRow: 'Select {number}',
    closedNotSelectable: 'Closed orders cannot be assigned or re-prioritised.',
    column: {
      number: 'Number',
      title: 'Title',
      facility: 'Facility',
      equipment: 'Equipment',
      /** The register's asset column (mockup): photo, name, tag. */
      asset: 'Asset',
      location: 'Location',
      priority: 'Priority',
      assignee: 'Assignee',
      status: 'Status',
      /** Age of a live order; the date for a closed one. */
      age: 'Age',
      due: 'Due',
      /** Phase 3.2. */
      workType: 'Work type',
    },
    unassigned: 'unassigned',
    /** F8.2 — a person working for a team. */
    assigneeWithTeam: '{name} ({team})',
    reassign: 'Reassign',
    overdue: 'Overdue',
    /** Age of a live work order, in the register's Opened column. */
    ageDays: '{days}d',
    /**
     * The Due cell (Fiix audit, Phase 0 item 2). A live order reads relative
     * to now — "2 d overdue", "Today", "Tomorrow" — because the register is
     * sorted on it and a planner scans it for what has slipped; a closed
     * order shows nothing, its due date no longer being a fact anyone acts on.
     * `overdueBy` is a plural record for the languages that inflect "day".
     */
    dueOverdueBy: { one: '{count} d overdue', other: '{count} d overdue' },
    dueToday: 'Today',
    dueTomorrow: 'Tomorrow',
    noDue: 'No due date',
    /** Row overflow menu (Phase 0 item 1): every row action lives here. */
    rowMenu: 'More actions for {number}',
    openRecord: 'Open',
    emptyTitle: 'No work orders',
    emptyDescription: 'Plans generate them automatically; downtime dispatches corrective ones.',
  },

  plans: {
    add: '+ New plan',
    nextDue: 'next {date}',
    /** The meter reading beside a METER_HOURS plan. */
    meter: 'meter {current} / {threshold} h',
    meterAwaiting: 'awaiting reading',
    meterThresholdPending: 'threshold pending',
    generate: 'Generate WO now',
    /** FF-AUDIT-039 (owner decision, option B): warn, do not refuse. */
    generateAnywayTitle: 'This plan already has an open work order',
    generateAnywayBody:
      '{woNumber} ({status}) is still open for this plan. Generating another sends someone to do the same job twice — the plan only moves on when that order completes. Create another anyway?',
    generateAnywayKeep: 'Keep the open order',
    generateAnywayGo: 'Create another order',
    /** FF-QA-149: the toast after "Generate WO now". */
    generated: '{number} created — find it on the Work orders tab.',
    pause: 'Pause',
    resume: 'Resume',
    paused: 'Paused',
    /** FF-AUDIT-20260914-210459-039: generation is paused behind the open
     * order; the card names it and counts the occurrences missed meanwhile. */
    waitingOn: 'No new work order until',
    waitingOnAfter: 'is completed',
    missed: {
      one: '{count} occurrence missed meanwhile',
      other: '{count} occurrences missed meanwhile',
    },
    /**
     * The two due states, named apart — UI plan Phase 8, workstream 3.
     *
     * A meter plan can be in either, and they mean different things to the
     * person reading the register: `Meter due` is "the machine has run its
     * hours"; `Date due` is "the safety net expired because it barely ran".
     * Showing one badge for both would make a lightly-used asset's appearance
     * on the list unexplainable.
     */
    meterDue: 'Meter due',
    dateDue: 'Date due',
    /**
     * On a meter plan the calendar date is the FALLBACK, not the due date.
     * The register printed `next {date}` here, which claims the job is due
     * then — when what the data means is "this is when we stop waiting for the
     * meter". Two strings, because they are two different facts.
     */
    fallbackOn: 'fallback {date}',
    /** How much run-time is left before the threshold. */
    meterRemaining: '{hours} h to go',
    emptyTitle: 'No plans yet',
    emptyDescription: 'Build one and work orders generate themselves forever.',
    /** Fiix audit Phase 1: the Plans REGISTER (columns, filters, row menu). */
    region: 'Maintenance plans',
    searchPlaceholder: 'Search plans by number, name or asset…',
    column: {
      /** F18: `PM-2026-00012`. */
      number: 'Number',
      name: 'Plan',
      equipment: 'Equipment',
      frequency: 'Frequency',
      nextDue: 'Next due',
      priority: 'Priority',
      assignee: 'Assignee',
      state: 'State',
    },
    active: 'Active',
    stateAll: 'Active and paused',
    /** The register's short form of "no new work order until {number} completes". */
    waitingBadge: 'Waiting on {number}',
    /** The register badge; the number is in its tooltip (one line per row). */
    waitingShort: 'Waiting',
    edit: 'Edit plan',
    openWaitingOrder: 'Open {number}',
    rowMenu: 'More actions for {name}',
  },

  /** The plan builder dialog. */
  planBuilder: {
    /** F8.2. */
    team: 'Team (optional)',
    noTeam: 'No team',
    teamHint: 'With a technician, the team shares the work; with none, each order is offered to the team’s members.',
    title: 'New maintenance plan',
    name: 'Name (e.g. Quarterly lubrication)',
    namePlaceholder: 'Name (e.g. Quarterly lubrication)*',
    equipment: 'Equipment',
    frequency: 'Frequency:',
    /** Phase 3.2: the kind of work and the planned effort, copied onto every generated order. */
    workType: 'Work type:',
    workTypeNone: 'Not set',
    estimate: 'Estimate:',
    estimatePlaceholder: 'minutes',
    frequencyInterval: 'Frequency interval',
    frequencyUnit: 'Frequency unit',
    /** METER_HOURS has no calendar due date, so its date field means something else. */
    firstDue: 'First due:',
    fallbackDue: 'Fallback due:',
    /** F24A — the counter a METER plan follows. */
    counterMetric: 'Counter',
    chooseCounter: 'Choose a counter',
    counterHint: 'The plan comes due each time this counter has gone up by the number above (for example every 5,000 cycles).',
    /** The built-in counters' names (a company's own counters show their own names). */
    builtInCounter: { usage_hours: 'Usage hours', cycle_count: 'Cycle count' },
    priority: 'Priority:',
    firstMeterThreshold: 'First meter threshold (optional)',
    firstMeterPlaceholder: 'Current meter + interval',
    maxDays: 'Max days between service (optional)',
    maxDaysPlaceholder: 'No time fallback',
    assignment: 'Assignment:',
    /* Audit 08-12 D2 item 3 + E3's constraint. "Engine picks per work order"
       named an internal component to a user and said nothing about WHO gets
       picked; the wording audit forbids "engine picks" specifically. This says
       what happens instead, and now that a department constraint exists it can
       honestly say the work goes to maintenance. */
    engineMode: 'Choose someone for each work order',
    engineModeHint:
      'Each work order goes to an available maintenance worker who is qualified for the asset and least loaded.',
    fixedMode: 'Always the same person',
    fixedModeHint: 'One named technician receives every work order this plan creates.',
    fixedTechnician: 'Technician (qualified and available people are suggested first)',
    /** Blocks submit in FIXED mode — the mode is meaningless without one. */
    fixedRequired: 'Choose the technician before saving, or switch to “Choose someone for each work order”.',
    department: 'Maintenance department:',
    departmentAny: 'Not set — anyone eligible may be chosen',
    /** FF-AUDIT-20260914-210459-038: the empty option says what is actually in
     * force. The old label promised "anyone eligible" while the exclusions
     * below it read "Not in the department this work belongs to". */
    departmentAnyInferred: 'Not set — using “{name}”, inferred from its name',
    departmentAnyStale: 'Not set — the department chosen before is archived',
    departmentHint:
      'Work orders are offered only to workers in this department. Leave it unset and anyone otherwise eligible may be chosen.',
    departmentInferred: 'Work orders are offered only to workers in “{name}”, the one active department named Maintenance. Choose one here to make it explicit.',
    departmentStale: 'The department chosen earlier has been archived, so anyone eligible may be chosen until you pick another.',
    departmentSaveFailed: 'Could not save the maintenance department.',
    /* Audit 08-12 D2 item 4 — naming a technician the gate rejects, most often
       one outside the maintenance department. Worded as a decision being
       recorded rather than a warning being dismissed, matching the check-plan
       wizard. `{reason}` is a STANDALONE sentence from the assignment
       catalogue, so it is set off with a dash rather than embedded. */
    overrideHeading: 'This person is not normally eligible',
    overrideExplain:
      '{name} was excluded — {reason}. You may assign them anyway; the reason below is recorded against the plan.',
    overrideReason: 'Why is this the right person?',
    overrideReasonPlaceholder:
      'e.g. Covering while the maintenance lead is on leave; supervised by the shift engineer.',
    overrideMissing: 'Give a reason before saving.',
    steps: 'Task steps:',
    stepDescription: 'Step {index} description',
    stepPlaceholder: 'Step {index}…',
    /** Reads inside "Reference photo 2 for step 3" — lowercase, mid-sentence. */
    stepLabel: 'step {index}',
    removeStep: 'Remove step',
    /** F5.1 */
    stepPhotoMode: 'Photo on step {index}',
    addStep: '+ Add step',
    cancel: 'Cancel',
    submit: 'Create plan',
    failed: 'Could not create plan.',
    /* Audit 08-12 D1.1 — the same builder now corrects an existing plan.
       Before this the Plans tab had no way in at all, so a plan created with
       the wrong frequency, procedure or parts could only be paused and
       rebuilt. */
    editTitle: 'Edit maintenance plan',
    loading: 'Loading the plan…',
    loadFailed: 'This plan could not be loaded.',
    save: 'Save plan',
    saveFailed: 'Could not save the plan.',
    description: 'Description (optional)',
    descriptionPlaceholder: 'What this plan covers, and anything the technician should know.',
    /** The equipment a plan services cannot be changed — see the note below. */
    equipmentLocked: 'Equipment cannot be changed',
    equipmentLockedHint:
      'A plan’s history, its meter readings and every work order it has generated belong to this asset. Create a plan on the other asset instead.',
    nextDue: 'Next due:',
    planActive: 'Plan is active',
    planActiveHint: 'A paused plan keeps its history and stops generating work orders.',
    parts: 'Required parts:',
    partsHint: 'Copied onto every work order this plan generates.',
    partQuantity: 'Quantity for part {index}',
    removePart: 'Remove part',
    addPart: '+ Add part',
    partPickerLabel: 'Part {index}',
  },

  /** The manual "+ New Work Order" dialog. */
  newWorkOrder: {
    title: 'New work order',
    workOrderTitle: 'Title (what needs doing)',
    titlePlaceholder: 'Title (what needs doing)*',
    equipment: 'Equipment',
    priority: 'Priority:',
    due: 'Due (optional — defaults by priority):',
    /** Phase 3.3: the quick-create stays four fields; the rest folds away. */
    moreOptions: 'More options',
    fewerOptions: 'Fewer options',
    /** Owner request 2026-09-19: steps and reference documents at creation. */
    steps: 'Steps',
    stepsPlaceholder: 'One step per line — tools, safety notes and documents can be added on the work order afterwards',
    stepsHint: { one: '{count} step', other: '{count} steps' },
    documents: 'Reference documents',
    workType: 'Work type:',
    workTypeNone: 'Not set',
    estimate: 'Estimate:',
    estimatePlaceholder: 'minutes',
    notes: 'Description / notes',
    notesPlaceholder: 'Description / notes (optional)…',
    /** FF-AUDIT-036 */
    duePast: 'This due date is already past — the order will be overdue the moment it is created.',
    assignAfterEquipment: 'Choose the equipment first — who can take this depends on the asset, its location and its department.',
    assignTo: 'Assign to (optional — qualified + available suggestions)',
    /** F20.5: who does it — "Me" by default; the rest as the assignment rules allow. */
    assignMode: 'Who does it',
    assignModes: { ME: 'Me', OTHER: 'Someone else', TEAM: 'A team', ENGINE: 'Let dispatch choose', UNASSIGNED: 'Leave unassigned' },
    /** F8.2. */
    team: 'Team',
    chooseTeam: 'Choose a team…',
    teamHint: 'Any member of the team can take it from Available work; each member is told.',
    suggestedTeam: 'Suggested: {team} (this machine’s responsible team).',
    useSuggestedTeam: 'Give it to this team',
    /* The one-off twin of the plan builder's override block. Its own keys, not
       the plan's, for one word: a plan records the decision "against the plan"
       and this records it against THIS work order. Sharing the string would
       have put the wrong noun on one of the two screens — which is how a
       shared copy line quietly becomes wrong rather than merely reused. */
    overrideHeading: 'This person is not normally eligible',
    overrideExplain:
      '{name} was excluded — {reason}. You may assign them anyway; the reason below is recorded against this work order.',
    overrideReason: 'Why is this the right person?',
    overrideReasonPlaceholder:
      'e.g. Covering while the maintenance lead is on leave; supervised by the shift engineer.',
    overrideMissing: 'Give a reason before creating the work order.',
    cancel: 'Cancel',
    creating: 'Creating…',
    submit: 'Create work order',
    failed: 'Could not create the work order.',
    /** Fiix audit Phase 0: creating opens the record; the toast names it. */
    created: '{number} created.',
  },

  calendar: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    hint: 'Preventive plan dates and work orders share one view.',
    /** Only shown to someone who can actually reschedule. */
    dragHint: 'Drag an open work order to a new day, or select it to reschedule precisely.',
    region: 'Maintenance calendar',
    unknownEquipment: 'Equipment',
    more: '+{count} more',
    rescheduleTitle: 'Reschedule work order',
    dueAt: 'Due date and time',
    reason: 'Reason',
    reasonPlaceholder: 'Why is this due date changing?',
    cancel: 'Cancel',
    save: 'Save due date',
    failed: 'Could not reschedule work order.',
    /** Fiix audit Phase 3.4: week, agenda and workload views. */
    viewSwitcher: 'Calendar view',
    viewMonth: 'Month',
    viewWeek: 'Week',
    viewAgenda: 'Agenda',
    viewWorkload: 'Workload',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    weekHeading: '{from} – {to}',
    agendaHeading: 'Next 14 days · {from} – {to}',
    nothingDue: 'Nothing due',
    nothingDueFortnight: 'Nothing is due in the next two weeks.',
    forecast: 'Forecast',
    minutes: '{minutes} min',
    workloadRegion: 'Technician workload',
    workloadHint: 'Planned minutes per technician per day, against an eight-hour day. Orders without an estimate count as a job at 0 min.',
    technician: 'Technician',
    unassigned: 'Unassigned',
    weekTotal: 'Week',
    laneCell: '{jobs} · {minutes} min',
    hours: '{hours} h',
  },

  board: {
    /**
     * Column TITLES keyed by the status they hold. `Backlog` is deliberately
     * not `Open`: on a board the column names a pile of work, and the badge on
     * the card still says the status.
     */
    column: {
      OPEN: 'Backlog',
      IN_PROGRESS: 'In progress',
      ON_HOLD: 'On hold',
      COMPLETED: 'Completed',
    },
    columnHint: {
      OPEN: 'Not started',
      IN_PROGRESS: 'Being worked on',
      ON_HOLD: 'Blocked — needs someone',
      COMPLETED: 'Done',
    },
    /** Whole message, so the count and the column name can swap order. */
    columnLabel: {
      one: '{column}, {count} work order',
      other: '{column}, {count} work orders',
    },
    columnCount: '({count})',
    /** The move buttons, keyed by the status they move TO. */
    move: {
      IN_PROGRESS: 'Start',
      ON_HOLD: 'Put on hold',
      COMPLETED: 'Complete',
    },
    /** Leaving ON_HOLD is a resume, not a start — same target, different verb. */
    moveResume: 'Resume',
    noEquipment: 'No equipment',
    unassigned: 'unassigned',
    noHoldReason: 'No reason recorded',
    overdue: 'Overdue',
    empty: 'Nothing here',
    moved: '{number} moved to {column}',
    heldAnnouncement: 'Work order put on hold',
    moveRejected: 'That move was rejected.',
  },

  bulk: {
    /**
     * The floating bar's count sentence.
     *
     * This was `noun: 'work order'`, handed to `BulkActionBar` which glued a
     * count in front and an "s" on the end. French renders that as
     * "2 bon de travails selected" — the plural is on the wrong word and the
     * participle does not agree. The whole sentence lives here now, in the
     * catalogue that owns the noun.
     */
    barSelected: { one: '{count} work order selected', other: '{count} work orders selected' },
    assign: 'Assign…',
    setPriority: 'Set priority…',
    /** Phase 3.7. */
    setDue: 'Due date…',
    setWorkType: 'Work type…',
    exportSelected: 'Export selected',
    cancelOrders: 'Cancel…',
    assignTitle: 'Assign selected work orders',
    priorityTitle: 'Set priority on selected work orders',
    dueTitle: 'Reschedule selected work orders',
    workTypeTitle: 'Set the work type on selected work orders',
    cancelTitle: 'Cancel selected work orders',
    dueDate: 'New due date',
    workType: 'Work type',
    reason: 'Reason',
    reasonPlaceholder: 'Why — recorded on each order',
    cancelWarning: 'Each open order is cancelled with this reason; closed orders are reported back, not changed.',
    selected: {
      one: '{count} work order selected. Each one is checked separately — anything you cannot change is reported back rather than silently skipped.',
      other:
        '{count} work orders selected. Each one is checked separately — anything you cannot change is reported back rather than silently skipped.',
    },
    assignTo: 'Assign to',
    priority: 'Priority',
    cancel: 'Cancel',
    applying: 'Applying…',
    apply: 'Apply to {count}',
    failed: 'The batch could not be applied.',
    resultTitle: 'Bulk update result',
    /** Per-record outcome. "38 of 40 updated" is actionable; one throw is not. */
    result: '{succeeded} updated, {failed} not changed.',
    rowFailed: 'Failed.',
    close: 'Close',
  },

  /** The technician runner. */
  /** F5.1: a step's camera. */
  photoMode: {
    help: 'Off shows no camera on the step (a photo is still possible from its ⋯ menu). Required blocks Done until a photo is taken.',
    options: { OFF: 'Off', OPTIONAL: 'Optional', REQUIRED: 'Required' },
  },
  run: {
    loading: 'Loading…',
    notFound: 'Work order not found.',
    /** The header line: asset · how it was raised · who has it. */
    description: '{equipment} · {source} · assigned to {assignee}',
    nobodyYet: 'nobody yet',
    /**
     * WHY this job exists — UI plan Phase 8, workstream 3.
     *
     * Phrased as a cause ("the meter reached its limit"), not as a category
     * ("meter"), because the badge sits beside Status and Priority and a
     * one-word `Meter` there reads as another attribute of the job rather than
     * the reason it was raised. Only ever shown on plan-generated work.
     */
    pmTrigger: {
      CALENDAR: 'Raised on schedule',
      METER: 'Raised by the hour meter',
    },
    overdue: 'Overdue',
    viewEquipment: 'View equipment →',
    /** Phase 2: the record header's overflow menu. */
    moreActions: 'More actions for this work order',
    /** Phase 3.3: open the plan builder seeded from this order. */
    makeRecurring: 'Make recurring…',
    /**
     * The facts strip under the pills (Fiix audit Phase 0, item 5). The
     * runner showed no due date, location, tag, plan or creation stamp
     * anywhere; until the record rail lands (Phase 2) these are the facts a
     * person at the machine and a reviewer both ask for first.
     */
    facts: {
      region: 'Work order facts',
      due: 'Due',
      noDue: 'No due date',
      overdueBy: { one: '{count} d overdue', other: '{count} d overdue' },
      dueToday: 'due today',
      asset: 'Asset',
      tag: 'Tag',
      noTag: 'no tag',
      location: 'Location',
      plan: 'Plan',
      source: 'Raised',
      created: 'Created',
      started: 'Started',
      completed: 'Completed',
      reported: 'Reported time',
      minutes: '{minutes} min',
      /** Phase 3.2. */
      workType: 'Work type',
      estimate: 'Estimate',
      /** Planned vs timed labour, side by side: "45 min planned · 12 min timed". */
      estimateVsTimed: '{planned} min planned · {timed} min timed',
      laborCost: 'Labour cost (est.)',
      /** No wage on file for the people who timed this job — never a false zero. */
      laborCostUnknown: 'No wage on file',
    },
    taking: 'Taking…',
    takeOwnership: 'Take ownership',
    evidenceBundle: 'Evidence bundle',
    start: 'Start',
    putOnHold: 'Put on hold',
    resume: 'Resume work',
    complete: 'Complete work order',
    cancelWorkOrder: 'Cancel WO',
    /** FF-AUDIT-052: the cancel dialog. */
    cancelDialog: {
      title: 'Cancel {number}?',
      body: 'The order leaves the queue and cannot be reopened. Say why — the reason is recorded on the order and in the audit log.',
      reason: 'Reason',
      reasonPlaceholder: 'e.g. duplicate of WO-2026-00041; raised on the wrong asset',
      keep: 'Keep the order',
      confirm: 'Cancel the order',
      cancelling: 'Cancelling…',
    },
    actionFailed: 'Action failed.',
    onHold: 'On hold',
    /** F9: who, when and why — "On hold since 12 Sep by Jane Doe — Waiting for parts: bearing on order". */
    heldSinceBy: 'On hold since {at} by {name} — {reason}',
    heldSince: 'On hold since {at} — {reason}',
    heldNoReason: 'No reason was recorded when this was put on hold.',
    /** FF-AUDIT-053: what the failed check said, on the order itself. */
    whyHeading: 'Why this job was raised',
    /** FF-AUDIT-007: the description entered at creation (or the plan's), on
     * every order, in every state. */
    descriptionHeading: 'Job description',
    noDescription: 'No description was entered for this work order.',
    completionNotesHeading: 'Completion notes',
    issuesFoundHeading: 'Issues found',
    /** FF-AUDIT-035: what became of the issues. */
    followUpRaised: 'Follow-up work order:',
    raiseFollowUp: 'Raise a follow-up work order',
    raisingFollowUp: 'Raising…',
    reopen: 'Reopen',
    noCompletionNotes: 'No completion notes were recorded.',
    stepsLeft: {
      one: '{count} step left — every step must be ticked or skipped with a note before completion.',
      other:
        '{count} steps left — every step must be ticked or skipped with a note before completion.',
    },
    tickStep: 'Tick step',
    untickStep: 'Untick step',
    tools: 'Tools: {tools}',
    skipped: 'Skipped: {note}',
    /** Reads inside "Reference photo 2 for step 3". */
    stepLabel: 'step {position}',
    skipReason: 'Why is this step being skipped?',
    skipReasonPlaceholder: 'Why is this step being skipped?*',
    skip: 'Skip',
    skipWithNote: 'Skip with note…',
    /** F5.3: photos on a step. */
    stepPhoto: 'Photo',
    stepPhotoRequired: 'Photo required',
    addPhoto: 'Add photo',
    stepMenu: 'More actions for step {position}',
    stepPhotoNeeded: 'This step needs a photo. Take one, then tick the step.',
    /** F6.4: a problem flagged on a step. */
    flagProblem: 'Flag a problem',
    clearFlag: 'Clear the flag',
    flagged: 'Problem flagged: {note}',
    repairOrder: 'Repair {number}',
    completeWithFlagsTitle: 'Complete with flagged problems?',
    completeWithFlags: {
      one: '{count} step still has a problem flagged. Complete the work order anyway?',
      other: '{count} steps still have a problem flagged. Complete the work order anyway?',
    },
    completeAnyway: 'Complete anyway',
    flag: {
      title: 'Flag a problem on {step}',
      note: 'What is wrong?',
      notePlaceholder: 'e.g. belt frayed at the splice',
      photo: 'Add photo',
      photos: {
        one: '{count} photo added',
        other: '{count} photos added',
      },
      createRepair: 'Create a repair work order',
      createRepairHelp: 'A stopped machine always gets one, linked to its downtime.',
      cancel: 'Cancel',
      save: 'Flag the problem',
      next: 'Next',
    },
    stepPhotosWaiting: {
      one: '{count} photo waiting to upload',
      other: '{count} photos waiting to upload',
    },
    noStepsTitle: 'No steps',
    noStepsDescription: 'Ad-hoc work order — start, do the work, complete.',
    /** Owner request 2026-09-19: the people who raise orders write the steps. */
    noStepsDescriptionCanAdd: 'Ad-hoc work order — add the steps to follow, or start, do the work, complete.',
    addStep: 'Add step',
    editStep: 'Edit',
    removeStep: 'Remove',
    stepEditor: {
      addTitle: 'New step',
      editTitle: 'Edit step',
      description: 'Step',
      descriptionPlaceholder: 'What to do — one action per step',
      tools: 'Tools',
      toolsPlaceholder: 'e.g. 13 mm socket, torque wrench',
      safetyNotes: 'Safety note',
      safetyPlaceholder: 'e.g. lock out before opening the guard',
      documents: 'Reference documents',
      photoMode: 'Photo',
      cancel: 'Cancel',
      add: 'Add step',
      save: 'Save step',
      saving: 'Saving…',
    },
    partsConsumed: 'Parts consumed',
    partsToConsume: 'Parts to consume',
    partsHint: 'Pick a part to add it to the job; pick it again to add one more.',
    unknownPart: 'Part {id}…',
    onHand: '{count} on hand',
    insufficientStock: ' · insufficient stock',
    /** FF-AUDIT-20260921-210633-026: a completed order states what was consumed, not today's stock. */
    consumed: '{count} consumed',
    /** F10.1: planned parts — what the job is expected to need (they count as demand until used). */
    plannedMarker: 'Planned {count}',
    usedOfPlanned: '{used} used of {planned} planned',
    savePlan: 'Save as planned parts',
    savingPlan: 'Saving…',
    planSaved: 'Planned parts saved — they count as demand until used.',
    planUnsaved: 'Not saved as the plan yet — until it is, these parts do not count as demand.',
    shortAtRelease: 'Short by {count} when planned',
    quantity: 'Quantity',
    quantityFor: 'Quantity of {part}',
    oneFewer: 'One fewer',
    oneMore: 'One more',
    removePart: 'Remove',
    addAnotherPart: 'Add a part',
    addPart: 'Add',
    reportedDuration: 'Reported duration: {minutes} min.',
    timedDiffers: '(the stopwatch recorded {minutes} min)',
    files: 'Work order files',
    attach: 'Attach files',
    /** Owner request 2026-09-19: documents already in the register, linked rather than uploaded again. */
    linkDocuments: 'Link documents',
    linkedDocuments: 'Linked documents',
    linkDialog: {
      title: 'Link documents to {number}',
      hint: 'Manuals, procedures and warranties already in Documents. Everyone who opens this work order sees the ones they may read.',
      cancel: 'Cancel',
      save: 'Save links',
      saving: 'Saving…',
    },
    filesFailed:
      'The file list could not be loaded. Nothing has been lost — reopen the work order to try again.',
    loadingFiles: 'Loading files…',
    noFiles: 'No direct work order files.',
  },

  /** FF-AUDIT-20260914-210459-035: a completion by mistake has a way back. */
  reopen: {
    title: 'Reopen this work order',
    hint: 'It goes back to In progress. Parts already consumed, the service-history entry and the plan’s next due date stay as they are; the reason is recorded in the activity.',
    reason: 'Reason',
    reasonPlaceholder: 'Completed by mistake / the fix did not hold',
    cancel: 'Cancel',
    reopening: 'Reopening…',
    submit: 'Reopen',
    failed: 'Could not reopen it.',
  },

  hold: {
    title: 'Put this work order on hold',
    hint: 'The reason appears on the board and on the work order. “Waiting for parts” is worth more to the next person than a status change on its own.',
    /** F9 (OD-9): a category from a fixed list, then the person's own words. */
    category: 'Why is it on hold?',
    categoryPlaceholder: 'Choose a reason',
    reasonCode: {
      WAITING_PARTS: 'Waiting for parts',
      WAITING_VENDOR: 'Waiting for a vendor',
      WAITING_ACCESS: 'Waiting for access to the equipment',
      WAITING_APPROVAL: 'Waiting for approval',
      SAFETY: 'Safety concern',
      OTHER: 'Other',
    },
    detail: 'Details (optional)',
    detailRequired: 'Details',
    detailPlaceholder: 'e.g. bearing on order, due Thursday',
    /** The category and the note as one line: "Waiting for parts: bearing on order". */
    withNote: '{label}: {note}',
    cancel: 'Cancel',
    holding: 'Holding…',
    submit: 'Put on hold',
    failed: 'Could not put it on hold.',
  },

  /** F9: taking it off hold — back to where it was, with an optional note for the activity. */
  resume: {
    title: 'Resume this work order',
    hintOpen: 'It goes back to Open. A note is optional; it is kept in the activity.',
    hintInProgress: 'It goes back to In progress. A note is optional; it is kept in the activity.',
    note: 'Note (optional)',
    notePlaceholder: 'e.g. part arrived, back on it',
    cancel: 'Cancel',
    resuming: 'Resuming…',
    submit: 'Resume',
    failed: 'Could not resume it.',
  },

  complete: {
    title: 'Complete work order',
    /** FF-AUDIT-034: this is the REPORTED duration — the technician's word,
     * which MTTR, wrench time and cost use. The timer is a helper, named
     * separately below it so the two cannot be mistaken for one number. */
    duration: 'Reported duration:',
    durationTimedHint: 'The stopwatch recorded {minutes} min. Change it if the timer missed some of the work.',
    durationWallClockHint: 'No timer was used; this is the time since the work order was started, including any wait.',
    minutes: 'min',
    notes: 'Notes',
    notesPlaceholder: 'Notes…',
    issues: 'Issues found',
    issuesHelp: 'Anything discovered gets recorded.',
    issuesPlaceholder: 'Issues found…',
    /**
     * FF-QA-090 (owner decision 2026-09-27): the follow-up order is previewed
     * — title, priority, due date — and confirmed before it exists. It used to
     * be raised silently with the first line of the issues as a 120-character
     * title, the parent's priority and a 24 h deadline, so "needs replacement
     * next PM" became a critical 24-hour job.
     */
    followUp: {
      heading: 'Follow-up work order',
      create: 'Create a follow-up work order',
      createHint: 'A corrective order on the same asset; the issues above become its job description.',
      title: 'Title',
      /** The pre-filled title; the server uses the same form when a client sends none. */
      defaultTitle: 'Follow-up: {number}',
      priority: 'Priority',
      dueAt: 'Due',
      dueAtHelp: 'Clear it for no due date.',
    },
    partsWarning: {
      /** FF-QA-092: `{units}` is the formatted total quantity; the noun stays on the parts. */
      one: 'Completion will consume a total of {units} from {count} part.',
      other: 'Completion will consume a total of {units} from {count} parts.',
    },
    /** FF-AUDIT-20260914-210459-033: the pre-check, one line per part, facility-scoped. */
    partLine: '{part}: {needed} needed, {atFacility} at {facility}',
    partLineUnknownStock: '{part}: {needed} needed',
    partsShort: 'Not enough stock at this facility for the lines marked. Lower the quantity, transfer stock in, or remove the line.',
    /** The server's INSUFFICIENT_STOCK detail, keyed on its fields. */
    insufficientDetail: '{part}: {needed} needed, {atFacility} at {facility} ({total} company-wide)',
    thisFacility: 'this facility',
    cancel: 'Cancel',
    submit: 'Complete',
    failed: 'Could not complete.',
  },

  labor: {
    region: 'Labor timer',
    heading: 'Labor',
    headingTimed: 'Timed labour',
    /** Spoken, not the digits — "2:07:14" is read out as a date. */
    yourTimerRunning: 'Your timer: {elapsed}, running',
    yourTimerPaused: 'Your timer: {elapsed}, paused',
    recorded: 'Timed labour: {elapsed}',
    paused: 'Paused',
    running: 'Running',
    total: '{elapsed} recorded on this work order in total',
    startTimer: 'Start timer',
    pause: 'Pause',
    resume: 'Resume',
    /** Completes the button for a screen reader — the words follow "Pause". */
    theTimer: 'the labor timer',
    stop: 'Stop',
    theTimerAndRecord: 'the labor timer and record the time',
    stoppedOnCompletion: 'Completing the work order stopped any running timers.',
    failed: 'That did not work.',
    /**
     * The spoken duration, assembled from parts. Under a minute it says
     * SECONDS — rounding 24 seconds to "0 minutes" reads as "nothing was
     * recorded" on a timer that plainly just ran.
     */
    spokenSeconds: { one: '{count} second', other: '{count} seconds' },
    spokenHours: { one: '{count} hour', other: '{count} hours' },
    spokenMinutes: { one: '{count} minute', other: '{count} minutes' },
  },

  evidence: {
    heading: 'Evidence',
    hint: 'Pick what the next capture is, then take the photo or record a memo.',
    contextSwitcher: 'What the next capture is evidence of',
    /** `{context}` is `attachments`' own lowercase short form. */
    addPhoto: 'Add {context} photo',
    recordMemo: 'Record memo',
    remove: 'Remove {fileName}',
  },

  stepPhotos: {
    label: 'Reference photo',
    alt: 'Reference photo {index} for {step}',
    remove: 'Remove reference photo {index} for {step}',
    limit: {
      one: '{count} photo is the limit for one step — remove one to add another.',
      other: '{count} photos is the limit for one step — remove one to add another.',
    },
  },

  reassign: {
    title: 'Reassign work order',
    /** Whole sentence: the assignee clause has to move in French. */
    describedAssigned: '{number} · {title} — currently assigned to {assignee}.',
    describedUnassigned: '{number} · {title} — currently unassigned.',
    assignTo: 'Reassign to',
    reason: 'Reason (optional — recorded)',
    reasonPlaceholder: 'Why is this changing hands?',
    cancel: 'Cancel',
    reassigning: 'Reassigning…',
    submit: 'Reassign',
    failed: 'Could not reassign.',
    /** F8.2. */
    team: 'Team',
    noTeam: 'No team',
    teamHint: 'Choose a person from the team, or nobody — then any member can take it.',
    submitTeam: 'Give it to the team',
    teamsFailed: 'Could not load the teams. Close and try again.',
  },

  /**
   * The activity stream's sentences.
   *
   * The SERVER sends facts — a stable `type` plus resolved `data` values — and
   * the sentence is composed on the client precisely so it can be translated;
   * a pre-rendered English string in an append-only table could not be. Each
   * event has a with-detail and a without-detail form rather than a base
   * sentence plus an appended fragment, because the em dash and the clause
   * order are the translator's.
   */
  activity: {
    heading: 'Activity',
    count: '{count} recorded',
    emptyTitle: 'Nothing recorded yet',
    emptyDescription: 'Status changes, comments and files show up here as the job progresses.',
    loading: 'Loading…',
    showEarlier: 'Show earlier activity',
    someone: 'Someone',
    /** FF-AUDIT-20260921-210633-027: a playbook's rows name the playbook, and the person whose action set it off. */
    automationAuthor: 'Automation · {rule}',
    triggeredBy: 'triggered by {name}',
    /** An unrecognised type still renders — a silent hole claims nothing happened. */
    fallback: 'Updated this work order',
    createdWithSource: 'Raised this work order ({source})',
    created: 'Raised this work order',
    assignedTo: 'Assigned it to {assignee}',
    assigned: 'Changed the assignee',
    started: 'Started the work',
    heldWithReason: 'Put it on hold — {reason}',
    held: 'Put it on hold',
    resumedWithNote: 'Resumed the work — {note}',
    resumed: 'Resumed the work',
    laborStarted: 'Started their timer',
    laborStopped: 'Stopped their timer',
    rescheduledTo: 'Moved the due date to {at}',
    rescheduled: 'Changed the due date',
    completedIn: 'Completed it in {minutes} min',
    completed: 'Completed the work order',
    cancelledWithReason: 'Cancelled it — {reason}',
    cancelled: 'Cancelled it',
    overdue: 'Became overdue',
    /** Audit-sourced rows (FF-AUDIT-068): a priority change has its own line. */
    priorityChangedTo: 'Changed the priority to {priority}',
    priorityChanged: 'Changed the priority',
    /** Owner request 2026-09-19: steps and linked documents written onto the order. */
    stepAdded: 'Added a step: {step}',
    stepAddedBare: 'Added a step',
    stepUpdated: 'Edited a step: {step}',
    stepUpdatedBare: 'Edited a step',
    stepRemoved: 'Removed a step',
    /** F6.4 */
    stepFlagged: 'Flagged a problem on “{step}”: {note}',
    stepFlaggedBare: 'Flagged a problem on a step',
    stepUnflagged: 'Cleared the flag on “{step}”',
    stepUnflaggedBare: 'Cleared a step’s flag',
    documentsChanged: 'Changed the linked documents',
    reopened: 'Reopened it',
    reopenedWithReason: 'Reopened it — {reason}',
    followUpRaised: 'Raised follow-up work order {number} for the issues found',
    commented: 'Commented',
    threadResolved: 'Marked a discussion resolved',
    commentDeleted: 'Deleted a comment',
    /** One sentence per attachment context — not `Attached {file} as {word}`. */
    attachedFile: 'a file',
    attached: 'Attached {file}',
    attachedBefore: 'Attached {file} as a before photo',
    attachedAfter: 'Attached {file} as an after photo',
    attachedFailure: 'Attached {file} as failure evidence',
    attachedReference: 'Attached {file} as reference',
  },

  /**
   * Work requests (Fiix audit Phase 3.1): the triage queue, the "Request work"
   * dialog, the triage dialog, the portal card and the PUBLIC page a stranger
   * reaches from a facility's link or QR. Status and urgency are server enums
   * owned here — the code is the key, never the server's English.
   */
  /** F19: the Requests section of Available work. */
  availableRequests: {
    heading: {
      one: 'Requests ({count})',
      other: 'Requests ({count})',
    },
    tableLabel: 'Work requests you can take',
    colEquipment: 'Machine',
    colSubmitted: 'Submitted',
    colAction: 'Take',
    noMachine: 'No machine named',
    needsMachine: 'This request names no machine — a supervisor must triage it first.',
    anonymous: 'Anonymous',
    take: 'Take it',
    empty: 'No open requests.',
  },
  requests: {
    title: 'Work requests',
    description: 'Problems reported from the floor, waiting for a supervisor to turn them into work orders.',
    region: 'Work requests',
    requestWork: '+ Request work',
    searchPlaceholder: 'Search number, title, reporter or place…',
    quick: { new: 'New', triaged: 'Triaged', all: 'All' },
    status: { NEW: 'New', TRIAGED: 'In triage', CONVERTED: 'Converted', REJECTED: 'Rejected' },
    urgency: { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', SAFETY: 'Safety' },
    channel: { APP: 'In app', PORTAL: 'Portal' },
    column: {
      number: 'Number',
      title: 'Title',
      facility: 'Facility',
      urgency: 'Urgency',
      where: 'Where',
      requester: 'Reported by',
      channel: 'Via',
      status: 'Status',
      outcome: 'Outcome',
      created: 'Reported',
    },
    requestSortField: { requestNumber: 'number', title: 'title', urgency: 'urgency', status: 'status', createdAt: 'reported' },
    filter: { status: 'Status', urgency: 'Urgency', facility: 'Facility', channel: 'Via', from: 'From', to: 'To' },
    rowMenu: 'More actions for {number}',
    open: 'Open',
    convert: 'Convert to work order…',
    reject: 'Reject…',
    openWorkOrder: 'Open work order {number}',
    emptyTitle: 'No work requests',
    emptyDescription: 'Anyone can report a problem with “Request work”; supervisors triage the queue here.',
    ageDays: '{days} d ago',
    rejectedBecause: 'Rejected: {reason}',
    convertedTo: 'Work order {number}',
    noAsset: 'No asset named',
    /** The record dialog (row → Open). */
    detail: {
      title: 'Request {number}',
      reportedBy: 'Reported by',
      via: 'Via',
      when: 'Reported',
      where: 'Where',
      asset: 'Asset',
      description: 'Description',
      noDescription: 'No description was given.',
      triagedBy: 'Triaged by {name}',
      close: 'Close',
    },
    /** "Request work" from inside the app. */
    request: {
      title: 'Request work',
      intro: 'Say what is wrong and where. Maintenance will review it and turn it into a work order.',
      what: 'What is wrong?',
      whatPlaceholder: 'e.g. Conveyor 4 squeals at start-up',
      urgency: 'How urgent does it feel?',
      urgencyHint: { LOW: 'Whenever convenient', NORMAL: 'Soon', HIGH: 'Slowing production', SAFETY: 'Someone could get hurt' },
      asset: 'Asset (optional)',
      where: 'Where (optional)',
      wherePlaceholder: 'e.g. Press 4, north bay',
      details: 'Details (optional)',
      detailsPlaceholder: 'What you saw, when it started, anything that helps…',
      cancel: 'Cancel',
      submit: 'Send request',
      sending: 'Sending…',
      failed: 'Could not send the request.',
      sent: 'Request {number} sent to maintenance.',
    },
    /** Triage: convert or reject. */
    triage: {
      convertTitle: 'Convert {number} to a work order',
      rejectTitle: 'Reject {number}',
      requestSummary: '“{title}” — reported by {name}',
      asset: 'Asset',
      assetNeeded: 'The request names no asset — choose the equipment this work order is for.',
      priority: 'Priority:',
      workType: 'Work type:',
      workTypeNone: 'Not set',
      due: 'Due (optional — defaults by priority):',
      assignTo: 'Assign to (optional — qualified + available suggestions)',
      assignAfterAsset: 'Choose the asset first — who can take this depends on it.',
      /** D30: shown when the triager may not let dispatch choose, so "unassigned" is not offered. */
      mustName: 'Choose who does it — your assignment rules do not let you leave a work order unassigned.',
      reason: 'Why is this request not being accepted?',
      reasonPlaceholder: 'e.g. Duplicate of WO-2026-00041; already fixed on Tuesday.',
      reasonHint: 'The reporter sees this reason.',
      cancel: 'Cancel',
      convert: 'Create work order',
      converting: 'Creating…',
      converted: '{number} is now work order {woNumber}.',
      reject: 'Reject request',
      rejecting: 'Rejecting…',
      rejected: '{number} rejected.',
      failed: 'Could not triage the request.',
      alreadyTriaged: 'Someone else triaged this request first.',
    },
    /** The per-facility public links (maintenance:manage). */
    portal: {
      title: 'Public request links',
      intro: 'Each facility gets a link and a QR code. Anyone who has it can report a problem without signing in — post the QR where people work.',
      off: 'The public portal is switched off. Every link answers “not found” until it is turned on in Settings → Features.',
      settingsLink: 'Open Features settings',
      on: 'The public portal is on.',
      noLink: 'No link yet',
      minted: 'Link created {date}',
      copy: 'Copy link',
      copied: 'Copied',
      qrAlt: 'QR code for the {facility} request link',
      downloadQr: 'Download QR',
      create: 'Create link',
      rotate: 'Rotate link',
      rotateConfirm: 'Rotate the link for {facility}? The current link and any printed QR stop working immediately.',
      rotated: 'New link created for {facility}.',
      failed: 'Could not update the link.',
    },
    /** The PUBLIC page — no app shell, no account. */
    public: {
      brand: 'FloorFile',
      heading: 'Report a problem',
      at: '{company} · {facility}',
      intro: 'Tell maintenance what is wrong. They will review it and, if you leave an email, tell you what happens next.',
      name: 'Your name',
      email: 'Your email (optional — to hear back)',
      what: 'What is wrong?',
      urgency: 'How urgent does it feel?',
      where: 'Where is it?',
      wherePlaceholder: 'e.g. Press 4, north bay',
      details: 'Details (optional)',
      submit: 'Send to maintenance',
      sending: 'Sending…',
      sentTitle: 'Thank you — request {number} is with maintenance.',
      sentBody: 'Keep the number if you need to ask about it. You can close this page.',
      another: 'Report another problem',
      notFoundTitle: 'This link is not active',
      notFoundBody: 'It may have been replaced, or the portal may be switched off. Ask a supervisor for the current link.',
      tooMany: 'Too many requests through this link right now — please try again in a minute.',
      failed: 'Could not send the request. Please try again.',
      loading: 'Loading…',
    },
  },
  /** F8.2 — a team's open work (the team page). */
  teamWork: {
    heading: 'Open work',
    table: 'The team’s open work orders',
    empty: 'This team has no open work orders.',
    colNumber: 'Number',
    colTitle: 'Title',
    colWho: 'Taken by',
    notTaken: 'Not taken yet',
    colPriority: 'Priority',
    colDue: 'Due',
    colStatus: 'Status',
  },
  /** F4 — work-order templates (Maintenance → Templates). */
  templates: {
    heading: 'Templates',
    description: 'Starting points for work orders and plans. Whatever you make from one is yours to change; editing a template later never changes what was made from it.',
    newTemplate: '+ New template',
    browseLibrary: 'Browse library',
    table: 'Work-order templates',
    empty: 'No templates yet. Start one from scratch, save a finished work order as a template, or pick one from the library.',
    colName: 'Name',
    colCategory: 'Category',
    colSteps: 'Steps',
    colParts: 'Parts',
    colSource: 'Source',
    colUpdated: 'Updated',
    source: { CUSTOM: 'Yours', LIBRARY: 'Library' },
    archived: 'Archived',
    rowMenu: 'More actions for {name}',
    edit: 'Edit',
    duplicate: 'Duplicate',
    archive: 'Archive',
    restore: 'Restore',
    createWorkOrder: 'Create work order',
    createPlan: 'Create plan',
    duplicated: 'Copied as “{name}”.',
    archivedDone: '“{name}” archived.',
    restoredDone: '“{name}” restored.',
    includeArchived: 'Include archived',
    search: 'Search templates',
    editor: {
      createTitle: 'New template',
      editTitle: 'Edit template',
      name: 'Template name',
      description: 'What it is for (optional)',
      woTitle: 'Work-order title',
      woDescription: 'Work-order description (optional)',
      category: 'Category (optional)',
      priority: 'Priority',
      workType: 'Work type',
      workTypeNone: 'Not set',
      estimate: 'Estimated minutes',
      steps: 'Steps',
      step: 'Step {index}',
      stepPlaceholder: 'Step {index}',
      tools: 'Tools (optional)',
      safety: 'Safety notes (optional)',
      removeStep: 'Remove step',
      addStep: '+ Add step',
      parts: 'Parts',
      partPicker: 'Part {index}',
      quantity: 'Quantity for part {index}',
      removePart: 'Remove part',
      addPart: '+ Add part',
      save: 'Save template',
      cancel: 'Cancel',
      failed: 'Could not save the template.',
      loading: 'Loading the template…',
      stepPhotoMode: 'Camera on step {index}',
      documents: 'Documents for the whole job',
    },
    startFrom: 'Start from a template',
    startFromNone: 'No template — start blank',
    startFromHint: 'Fills in the title, steps, parts and documents. Everything stays editable.',
    saveAsTemplate: 'Save as template',
    savedAsTemplate: 'Saved as the template “{name}”.',
    saveAsTemplateFailed: 'Could not save this work order as a template.',
  },
  /** F4 — the built-in library of work-order and check templates. */
  library: {
    title: 'Template library',
    description: 'Ready-made procedures and checklists based on published practice. Using one copies it into your company, where you can change anything.',
    search: 'Search the library',
    allCategories: 'All',
    table: 'Library templates',
    colName: 'Template',
    colCategory: 'Category',
    colFrequency: 'Typically',
    colSize: 'Size',
    colStandards: 'Reference',
    steps: { one: '{count} step', other: '{count} steps' },
    fields: { one: '{count} item', other: '{count} items' },
    minutes: 'About {count} min',
    installed: 'In use',
    preview: 'Preview',
    use: 'Use this template',
    open: 'Open',
    addCopy: 'Add another copy',
    alreadyTitle: 'Already in use',
    alreadyBody: 'Your company already has “{name}” from the library. Open it, or add a second copy you can change separately?',
    installing: 'Adding…',
    installedWo: '“{name}” added to your templates.',
    installedForm: '“{name}” added to your forms as a draft — review it, then publish.',
    failed: 'Could not add the template.',
    close: 'Close',
    back: 'Back to the library',
    loadingPreview: 'Loading the preview…',
    empty: 'Nothing in the library matches.',
    standardsLine: 'Reference: {standards}',
    safetyNote: 'Safety: {text}',
    toolsNote: 'Tools: {text}',
    required: 'Required',
    category: {
      ROTATING_EQUIPMENT: 'Rotating equipment',
      POWER_TRANSMISSION: 'Power transmission',
      LUBRICATION: 'Lubrication',
      CONVEYORS: 'Conveyors',
      COMPRESSED_AIR: 'Compressed air',
      HVAC: 'HVAC',
      ELECTRICAL: 'Electrical',
      FLUID_POWER: 'Hydraulics and pneumatics',
      MATERIAL_HANDLING: 'Material handling',
      FIRE_SAFETY: 'Fire safety',
      EMERGENCY: 'Emergency equipment',
      FACILITY: 'Building and grounds',
      QUALITY: 'Quality',
      SAFETY: 'Safety',
      GENERAL: 'General',
    },
    frequency: {
      EACH_USE: 'Before each use',
      DAILY: 'Daily',
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      SEMI_ANNUAL: 'Twice a year',
      ANNUAL: 'Yearly',
      AS_NEEDED: 'As needed',
    },
  },
})

export type MaintenanceCopy = typeof maintenanceCopy.en
